<script setup lang="ts">
    import PageHeader from "@/components/common/PageHeader.vue";
    import { ReceivePaymentsViewModel, UserSearchResult } from "@/typings";
    import ReceivePaymentsSearchForm from "@/components/OfflineTransactions/ReceivePaymentsSearchForm.vue";
    import ReceivePaymentsSectionGrid from "@/components/OfflineTransactions/ReceivePaymentsSectionGrid.vue";
    import { ApiRoutes } from "@/constants";
    import { readFiltersFromUrl, syncFiltersToUrl } from "@/composables/url-filters";
    import { computed, onMounted, ref } from "vue";

    const { receivePaymentsData } = defineProps<{
        receivePaymentsData: ReceivePaymentsViewModel;
    }>();

    const BACKEND_FIELDS = new Set(["associateName", "employeeNumber"]);

    // Split URL filters at setup time so the grid mounts with the already-filtered list,
    // avoiding any setData()-mid-build race against Tabulator.
    const urlFilters = readFiltersFromUrl();
    const initialFrontendFilters: Record<string, string> = {};
    const initialBackendFilters: Record<string, string> = {};
    for (const [key, value] of Object.entries(urlFilters)) {
        if (!value) continue;
        if (BACKEND_FIELDS.has(key)) initialBackendFilters[key] = value;
        else initialFrontendFilters[key] = value;
    }

    const isSearching = ref(false);
    const frontendFilters = ref<Record<string, string>>(initialFrontendFilters);

    // Workaround: spGetOfflineTransactions does not currently project CreatedByUser,
    // so we match on the createdBy FullName returned from /searchAssociates.
    // When Ioan adds CreatedByUser to the SP, switch this to a Set<string> of userIds.
    const matchedAssociateNames = ref<Set<string> | null>(null);
    const matchedPrimaryLocationIds = ref<Set<string> | null>(null);
    const matchedPrimaryGroupLocationIds = ref<Set<string> | null>(null);

    async function fetchMatchedAssociates(backendValues: Record<string, string>) {
        isSearching.value = true;
        try {
            const params = new URLSearchParams(backendValues);
            const response = await fetch(`${ApiRoutes.offlineTransactions.searchAssociates}?${params.toString()}`);
            if (!response.ok) {
                matchedAssociateNames.value = null;
                matchedPrimaryLocationIds.value = null;
                matchedPrimaryGroupLocationIds.value = null;
                return;
            }
            const result = await response.json();
            const users: UserSearchResult[] = result?.users ?? [];
            matchedAssociateNames.value = new Set(
                users.map(u => u.fullName).filter((n): n is string => !!n)
            );
            matchedPrimaryLocationIds.value = new Set(
                users.map(u => u.primaryLocationId).filter((id): id is string => !!id)
            );
            matchedPrimaryGroupLocationIds.value = new Set(
                users.map(u => u.primaryGroupLocationId).filter((id): id is string => !!id)
            );
        } catch {
            matchedAssociateNames.value = null;
            matchedPrimaryLocationIds.value = null;
            matchedPrimaryGroupLocationIds.value = null;
        } finally {
            isSearching.value = false;
        }
    }

    async function updateSearch(filters: Record<string, string>) {
        const backendValues: Record<string, string> = {};
        const frontendValues: Record<string, string> = {};

        for (const [key, value] of Object.entries(filters)) {
            if (value === undefined || value === null || value === "") continue;

            if (BACKEND_FIELDS.has(key)) {
                backendValues[key] = String(value);
            } else {
                frontendValues[key] = String(value);
            }
        }

        frontendFilters.value = frontendValues;
        // Pass the full shape, empties included, so cleared fields leave the URL.
        syncFiltersToUrl(filters);

        if (Object.keys(backendValues).length === 0) {
            matchedAssociateNames.value = null;
            matchedPrimaryLocationIds.value = null;
            matchedPrimaryGroupLocationIds.value = null;
            return;
        }

        await fetchMatchedAssociates(backendValues);
    }

    function handleSearchReset(clearedFilters: Record<string, string> = {}) {
        frontendFilters.value = {};
        matchedAssociateNames.value = null;
        matchedPrimaryLocationIds.value = null;
        matchedPrimaryGroupLocationIds.value = null;
        // The form emits its cleared shape so every filter key is dropped from the URL.
        syncFiltersToUrl(clearedFilters);
    }

    // If the URL included backend filters, fire the associate lookup once Tabulator is built.
    onMounted(() => {
        if (Object.keys(initialBackendFilters).length > 0) {
            void fetchMatchedAssociates(initialBackendFilters);
        }
    });
</script>

<template>
    <page-header variant="simple" title="Receive Payments" />

    <receive-payments-search-form
        :initial-filters="urlFilters"
        @update-search="updateSearch"
        @reset-search="handleSearchReset" />

    <receive-payments-section-grid
        :payments-data="receivePaymentsData.transactions"
        :parents-data="receivePaymentsData.locationParents"
        :locations-data="receivePaymentsData.locations"
        :frontend-filters="frontendFilters"
        :matched-associate-names="matchedAssociateNames"
        :matched-primary-location-ids="matchedPrimaryLocationIds"
        :matched-primary-group-location-ids="matchedPrimaryGroupLocationIds"
        :is-searching="isSearching" />
</template>
